import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, OnDestroy, OnChanges, SimpleChanges, Output} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {FroalaTemplateVariablesService} from '../../froala/froala-template-variables.service';
import {FroalaTemplateConditionsService} from '../../froala/froala-template-conditions.service';
import {FroalaToolbarPreviewService} from '../../services/froala-toolbar-preview.service';
import {flashFocus} from '../../services/focus-flash';

declare const window: any;

/**
 * Renders wysiwyg content as static html until the user interacts with the field.
 * Only then the actual froala editor gets initialized. Froala initialization is
 * expensive and large questionnaires create several hundred editor instances,
 * which made the editor slow to load and use (MYM-1029).
 */
@Component({
  selector: 'app-qa-lazy-froala',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './lazy-froala.component.html',
  styleUrls: ['./lazy-froala.component.css', '../../froala/template-tokens.css']
})
export class LazyFroalaComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Froala editor options. Must be referentially stable, as usual for the froala directive
   */
  @Input() options: any;

  /**
   * Wysiwyg content (html)
   */
  @Input() model: string;

  /**
   * Renders the static preview as block editor field (e.g. for descriptions)
   * instead of an inline field (titles, answers)
   */
  @Input() block = false;

  /** Enables runtime variable insertion for questionnaire property fields. */
  @Input() allowVariables = false;
  @Input() variableLocale = 'de_DE';
  public variableLabels: {[path: string]: string} = {};

  /** Requests focus when content in a deferred properties tab becomes available. */
  @Input() focusRequested = false;
  @Output() focusHandled = new EventEmitter<void>();

  /**
   * Emits when the user changed the content
   */
  @Output() modelChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Emits when the editor element loses focus. Mirrors the (blur) binding
   * that was previously placed directly on the froala element
   */
  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Is the real froala editor active (initialization started)?
   */
  public active = false;

  /**
   * Is the real froala editor fully initialized? The static preview stays
   * visible until then, so the swap happens without flickering
   */
  public ready = false;

  /**
   * Copy of the given options with a wrapped "initialized" event, so we can
   * focus the editor right after a user triggered activation
   */
  public wrappedOptions: any;

  /**
   * Toolbar html of a real froala editor for the static block preview.
   * Snapshot of the original toolbar, so the preview looks pixel perfect
   */
  public toolbarPreviewHtml: SafeHtml = null;

  /**
   * Should the editor get focused once it is initialized?
   */
  private pendingFocus = false;

  /**
   * Should the field pulse once it is focused? Set for focus requests from the preview, the focused
   * field alone is too subtle to find in the property panel
   */
  private pendingFlash = false;
  private pendingVariablePicker = false;
  private pendingConditionPicker = false;
  private pendingConditionIndex = -1;
  private pendingVariableIndex = -1;
  private editorInstance: any = null;
  private destroyed = false;

  /**
   * Character offset where the cursor should be placed after initialization.
   * Captured from the click position in the static preview. -1 means "end of content"
   */
  private pendingCaretOffset = -1;

  /**
   * Injects dependencies
   */
  public constructor(
    private ref: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private templateVariables: FroalaTemplateVariablesService,
    private toolbarPreview: FroalaToolbarPreviewService,
    private templateConditions: FroalaTemplateConditionsService
  ) {
  }

  /**
   * Builds the wrapped froala options once and loads the
   * toolbar preview for block editor fields
   */
  public ngOnInit() {
    this.refreshVariableLabels();
    this.wrappedOptions = this.buildWrappedOptions();
    this.block && this.loadToolbarPreview();
  }

  /**
   * Activates the froala editor. Optionally focuses it after initialization
   * and places the cursor at the clicked position (if a mouse event is given)
   * or at the end of the content.
   *
   * @param {boolean} focus
   * @param {MouseEvent} event Optional mouse event to derive the cursor position from
   */
  public activate(focus: boolean = true, event?: MouseEvent) {
    if ((event?.target as Element)?.closest('[data-qa-variable], [data-qa-condition]')) {
      event.preventDefault();
      return;
    }
    if (this.active) {
      return;
    }

    this.pendingFocus = focus;
    this.refreshVariableLabels();
    this.pendingVariablePicker = this.allowVariables && !!(event?.target as Element)?.closest('[data-cmd="qaInsertVariable"]');
    this.pendingConditionPicker = this.allowVariables && !!(event?.target as Element)?.closest('[data-cmd="qaInsertCondition"]');
    (this.pendingVariablePicker || this.pendingConditionPicker) && event.preventDefault();
    this.pendingCaretOffset = event ? this.getCaretOffsetFromEvent(event) : -1;
    this.active = true;
    this.ref.markForCheck();
  }

  /** Activates a lazy field when a condition boundary is clicked or keyboard-activated. */
  public editCondition(event: CustomEvent): void {
    if (this.active || !this.allowVariables) { return; }
    const root = (event.target as Element).closest('.fr-element');
    const tokens = Array.from(root.querySelectorAll('[data-qa-condition="if"]')) as HTMLElement[];
    this.pendingConditionIndex = tokens.findIndex(token => token.dataset.qaConditionId === event.detail.id);
    this.activate();
  }

  /** Activates a lazy field when a variable token is clicked and reopens it in the picker afterwards. */
  public editVariable(event: CustomEvent): void {
    if (this.active || !this.allowVariables) { return; }
    const root = (event.target as Element).closest('.fr-element');
    if (!root) { return; }
    event.preventDefault();
    this.pendingVariableIndex = Array.from(root.querySelectorAll('[data-qa-variable]')).indexOf(event.target as Element);
    this.activate();
  }

  /** Applies external focus after initialization and acknowledges the request. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.variableLocale) {
      this.refreshVariableLabels();
    }
    if (changes.focusRequested && this.focusRequested) {
      setTimeout(() => {
        if (!this.destroyed && this.focusRequested) {
          this.pendingFlash = true;
          this.focus();
          this.focusHandled.emit();
        }
      }, 0);
    }
  }

  /** Resolves the current readable variable names without rebuilding the catalog on each keystroke. */
  private refreshVariableLabels(): void {
    if (this.allowVariables) {
      this.variableLabels = this.templateVariables.getLabels(this.variableLocale);
    }
  }

  /** Focuses both lazy and already initialized editors from an external preview. */
  public focus(): void {
    if (this.destroyed) {
      return;
    }
    if (!this.active) {
      this.activate();
      return;
    }
    if (!this.ready) {
      this.pendingFocus = true;
      this.pendingCaretOffset = -1;
      return;
    }
    this.focusEditor(this.editorInstance);
  }

  /** Prevents deferred focus from returning to an editor whose panel was replaced. */
  public ngOnDestroy(): void {
    this.destroyed = true;
    this.editorInstance = null;
    this.pendingFocus = false;
  }

  /**
   * Event handler for content changes of the active editor
   *
   * @param {string} model
   */
  public onModelChange(model: string) {
    this.model = model;
    this.modelChange.emit(model);
  }

  /**
   * Loads the snapshotted froala toolbar html for the static block preview.
   * The html is generated by a real froala editor, so the sanitizer bypass
   * only ever renders markup that froala itself would render
   */
  private loadToolbarPreview() {
    this.toolbarPreview.getToolbarHtml(this.wrappedOptions).then((html) => {
      if (!html || this.ready) {
        return;
      }

      this.toolbarPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      this.ref.markForCheck();
    });
  }

  /**
   * Returns a copy of the given options where the "initialized" event first calls
   * the original handler (if any) and then applies the pending focus. The copy is
   * built once and stays referentially stable, so the froala directive does not
   * deep clone it on every change detection cycle.
   */
  private buildWrappedOptions(): any {
    const self = this;
    const options = Object.assign({}, this.options);
    options.events = Object.assign({}, this.options && this.options.events);

    if (this.block) {
      options.toolbarSticky = false;
      options.toolbarInline = false;
      const toolbar = this.buildCompactToolbar(options.toolbarButtons);
      if (toolbar) {
        // froala uses viewport breakpoints, but these editors live in narrow panels
        options.toolbarButtons = toolbar;
        options.toolbarButtonsMD = toolbar;
        options.toolbarButtonsSM = toolbar;
        options.toolbarButtonsXS = toolbar;
      }
    }

    if (this.allowVariables) {
      this.templateVariables.configure(options, () => this.variableLocale);
      this.templateConditions.configure(options, () => this.variableLocale);
    }

    const originalBlur = options.events['blur'];
    options.events['blur'] = function(event: any) {
      originalBlur && originalBlur.call(this, event);
      self.zone.run(() => self.blur.emit(event));
    };

    const originalInitialized = options.events['initialized'];

    options.events['initialized'] = function() {
      originalInitialized && originalInitialized.call(this);
      self.onEditorInitialized(this);
    };

    return options;
  }

  /** Keeps the primary text commands visible and groups every other configured action. */
  private buildCompactToolbar(toolbar: any): any {
    if (!toolbar) {
      return null;
    }
    if (Array.isArray(toolbar)) {
      return {moreText: {buttons: toolbar.filter(command => command !== '|' && command !== '-'), buttonsVisible: 3, align: 'left'}};
    }

    const compact: any = {};
    Object.keys(toolbar).forEach(group => {
      compact[group] = Object.assign({}, toolbar[group], {
        align: 'left',
        buttonsVisible: group === 'moreText' ? 3 : 0
      });
    });
    return compact;
  }

  /**
   * Swaps the static preview for the fully initialized editor and applies
   * a pending focus. Runs inside the angular zone, as froala events may
   * fire outside of it
   *
   * @param {any} editor Froala editor instance
   */
  private onEditorInitialized(editor: any) {
    this.zone.run(() => {
      this.editorInstance = editor;
      this.ready = true;
      this.ref.markForCheck();
      const caretOffset = (this.pendingVariablePicker || this.pendingConditionPicker) ? (editor.el.textContent || '').length : this.pendingCaretOffset;
      this.pendingFocus && this.focusEditor(editor, caretOffset);
      if (this.pendingVariablePicker) {
        this.pendingVariablePicker = false;
        setTimeout(() => {
          !this.destroyed && editor.commands.exec('qaInsertVariable');
        }, 0);
      }
      if (this.pendingVariableIndex >= 0) {
        const index = this.pendingVariableIndex;
        this.pendingVariableIndex = -1;
        // tokens get decorated a few frames after init, block editors take longer
        const open = (attempt: number) => setTimeout(() => {
          if (this.destroyed) { return; }
          const token = editor.el.querySelectorAll('[data-qa-variable]')[index];
          token ? token.dispatchEvent(new CustomEvent('qa-variable-edit', {bubbles: true, cancelable: true}))
            : attempt < 20 && open(attempt + 1);
        }, 40);
        open(0);
      }
      if (this.pendingConditionPicker || this.pendingConditionIndex >= 0) {
        const index = this.pendingConditionIndex;
        this.pendingConditionPicker = false;
        this.pendingConditionIndex = -1;
        setTimeout(() => {
          if (this.destroyed) { return; }
          const token = editor.el.querySelectorAll('[data-qa-condition="if"]')[index] as HTMLElement;
          if (index >= 0 && token) {
            token.dispatchEvent(new CustomEvent('qa-condition-edit', {bubbles: true, detail: {id: token.dataset.qaConditionId}}));
          } else if (index < 0) {
            editor.commands.exec('qaInsertCondition');
          }
        }, 40);
      }
      this.pendingFocus = false;
      this.pendingCaretOffset = -1;
    });
  }

  /**
   * Returns the character offset of the click position within the static
   * preview content, so the cursor can be restored at the same position in
   * the real editor. Returns -1 (end of content) if it cannot be determined
   *
   * @param {MouseEvent} event
   * @returns {number}
   */
  private getCaretOffsetFromEvent(event: MouseEvent): number {
    const container = <HTMLElement>event.currentTarget;
    const root = container && container.querySelector('.qa-lazy-froala');

    if (!root) {
      return -1;
    }

    const doc = <any>document;
    let node: Node = null;
    let offset = 0;

    // chrome, edge and safari
    if (doc.caretRangeFromPoint) {
      const range = doc.caretRangeFromPoint(event.clientX, event.clientY);
      range && (node = range.startContainer) && (offset = range.startOffset);
    // firefox
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(event.clientX, event.clientY);
      pos && (node = pos.offsetNode) && (offset = pos.offset);
    }

    // clicks outside of the content (e.g. on the toolbar preview) focus the end
    if (!node || !root.contains(node)) {
      return -1;
    }

    // measure the text length from content start to the caret position
    const measure = document.createRange();
    measure.selectNodeContents(root);
    measure.setEnd(node, offset);
    return measure.toString().length;
  }

  /**
   * Places the cursor at given character offset within the editor element.
   * Returns false if the offset could not be resolved
   *
   * @param {HTMLElement} rootEl Editor content element
   * @param {number} chars Character offset
   * @returns {boolean}
   */
  private setCaretAtTextOffset(rootEl: HTMLElement, chars: number): boolean {
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, null);
    let node: Node = walker.nextNode();

    while (node) {
      const length = node.textContent.length;

      if (chars <= length) {
        const selection = window.getSelection();
        const range = document.createRange();
        const token = node.parentElement.closest('[data-qa-variable], [data-qa-condition]');
        if (token) {
          chars === 0 ? range.setStartBefore(token) : range.setStartAfter(token);
        } else {
          range.setStart(node, chars);
        }
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
      }

      chars -= length;
      node = walker.nextNode();
    }

    return false;
  }

  /**
   * Focuses given froala editor instance and places the cursor at given
   * character offset, or at the end of the content if the offset is -1
   * or cannot be resolved.
   *
   * @param {any} editor Froala editor instance
   * @param {number} caretOffset Character offset for the cursor position
   */
  private focusEditor(editor: any, caretOffset: number = -1) {
    setTimeout(() => {
      if (this.destroyed || !editor || !editor.events || !editor.el) {
        return;
      }

      editor.events.focus();
      editor.el.focus({preventScroll: true});

      if (!(caretOffset >= 0 && this.setCaretAtTextOffset(editor.el, caretOffset))) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editor.el);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      this.revealCaret(editor.el);
      this.pendingFlash && this.flash();
    }, 0);
  }

  /** Pulses the visible field: the answer input or the text area of the editor box below its toolbar */
  private flash() {
    this.pendingFlash = false;
    const host = this.host.nativeElement;
    flashFocus(host.closest('.qa-fake-input') || host.querySelector('.fr-wrapper') || host);
  }

  /**
   * Scrolls the caret into view, also inside long texts: the scroll areas around the editor (e.g. the
   * properties panel) are moved just enough, scrollIntoView on the editor would only show its start
   *
   * @param {HTMLElement} element Editable element holding the caret
   */
  private revealCaret(element: HTMLElement) {
    const caret = this.getCaretRect(element) || element.getBoundingClientRect();
    const margin = 24;
    for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const overflowY = getComputedStyle(parent).overflowY;
      if ((overflowY !== 'auto' && overflowY !== 'scroll') || parent.scrollHeight <= parent.clientHeight) {
        continue;
      }
      const bounds = parent.getBoundingClientRect();
      const top = caret.top - margin - bounds.top;
      const bottom = caret.bottom + margin - bounds.bottom;
      const delta = top < 0 ? top : (bottom > 0 ? Math.min(bottom, top) : 0);
      if (delta) {
        parent.scrollTop += delta;
        caret.y -= delta;
      }
    }
    // narrow layouts scroll the page itself
    const pageDelta = caret.top < margin ? caret.top - margin
      : (caret.bottom > window.innerHeight - margin ? caret.bottom - window.innerHeight + margin : 0);
    pageDelta && window.scrollBy({top: pageDelta});
  }

  /**
   * Position of the caret; a caret at the end of an element has no box of its own,
   * then the character before it is measured
   *
   * @param {HTMLElement} element Editable element holding the caret
   */
  private getCaretRect(element: HTMLElement): DOMRect | null {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !element.contains(selection.anchorNode)) {
      return null;
    }
    const range = selection.getRangeAt(0).cloneRange();
    const rect = range.getClientRects()[0];
    if (rect && (rect.height || rect.top)) {
      return DOMRect.fromRect(rect);
    }

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let last: Text | null = null;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if ((node.textContent || '').length && range.comparePoint(node, 0) <= 0) {
        last = node as Text;
      }
    }
    if (!last) {
      return null;
    }
    const probe = document.createRange();
    probe.setStart(last, Math.max(0, last.length - 1));
    probe.setEnd(last, last.length);
    const rects = probe.getClientRects();
    return rects.length ? DOMRect.fromRect(rects[rects.length - 1]) : null;
  }
}
